import { useRef, useState, useEffect, useMemo } from "react";
// material-ui
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import CircularProgress from "@mui/material/CircularProgress";

// project import

import MainCard from "components/MainCard";
import Transitions from "components/@extended/Transitions";

import { ThemeMode } from "config";

import { useGetMenu } from "api/menu";
import {
  Icon,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";
import ReadOutlined from "@ant-design/icons/ReadOutlined";
import useAuth from "hooks/useAuth";
export type ILibrary = { id: string | number; name: string };
type SelectLibraryProps = {
  setLibrary?: (library: ILibrary | undefined) => void;
};
export default function SelectLibrary({ setLibrary }: SelectLibraryProps) {
  const { libraries, menuLoading, selectedLibrary } = useGetMenu();
  const { switchLogin } = useAuth();
  const theme = useTheme();

  const anchorRef = useRef<any>(null);
  const [open, setOpen] = useState(false);
  const [switchingId, setSwitchingId] = useState<string | number | null>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const onSelectLibrary = async (library: ILibrary) => {
    if (library.id != selectedLibrary) {
      setSwitchingId(library.id);
      try {
        await switchLogin(library?.id);
        window.location.reload();
      } catch (err) {
        setSwitchingId(null);
      }
    }
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const currentLibrary = useMemo(() => {
    return libraries?.find(
      (lib: { id: string | number; name: string }) =>
        String(lib.id) === String(selectedLibrary)
    );
  }, [libraries, selectedLibrary]);

  useEffect(() => {
    if (setLibrary && currentLibrary) {
      setLibrary(currentLibrary);
    }
  }, [currentLibrary, setLibrary]);

  const iconBackColorOpen =
    theme.palette.mode === ThemeMode.DARK ? "background.default" : "grey.100";

  return (
    <>
      {libraries != null && libraries.length > 1 ? (
        <Box sx={{ flexShrink: 0, ml: 0.75 }}>
          <Tooltip title={"Switch library"}>
            <ButtonBase
              sx={{
                p: 0.25,
                bgcolor: open ? iconBackColorOpen : "transparent",
                borderRadius: 1,
                "&:hover": {
                  bgcolor:
                    theme.palette.mode === ThemeMode.DARK
                      ? "secondary.light"
                      : "secondary.lighter",
                },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.secondary.dark}`,
                  outlineOffset: 2,
                },
              }}
              aria-label="open profile"
              ref={anchorRef}
              aria-controls={open ? "profile-grow" : undefined}
              aria-haspopup="true"
              onClick={handleToggle}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
                sx={{ p: 0.5 }}
              >
                {menuLoading || switchingId !== null ? (
                  <CircularProgress size={18} />
                ) : (
                  <Icon>
                    <ReadOutlined />
                  </Icon>
                )}
              </Stack>
            </ButtonBase>
          </Tooltip>
          <Popper
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            popperOptions={{
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 9],
                  },
                },
              ],
            }}
          >
            {({ TransitionProps }) => (
              <Transitions
                type="grow"
                position="top-right"
                in={open}
                {...TransitionProps}
              >
                <Paper
                  sx={{
                    boxShadow: theme.customShadows.z1,
                    width: 290,
                    minWidth: 240,
                    maxWidth: { xs: 250, md: 290 },
                  }}
                >
                  <ClickAwayListener onClickAway={handleClose}>
                    <MainCard elevation={0} border={false} content={false}>
                      {menuLoading ? (
                        <></>
                      ) : (
                        <Stack>
                          <List
                            component="nav"
                            sx={{
                              p: 0,
                              "& .MuiListItemIcon-root": { minWidth: 32 },
                            }}
                          >
                            {libraries.map((library: ILibrary, idx: number) => {
                              const isSelected =
                                selectedLibrary != null &&
                                String(library?.id) === String(selectedLibrary);
                              const isThisSwitching = switchingId === library.id;
                              return (
                                <ListItemButton
                                  key={idx}
                                  disabled={switchingId !== null}
                                  onClick={() => {
                                    onSelectLibrary(library);
                                  }}
                                  sx={{
                                    backgroundColor: isSelected
                                      ? theme.palette.primary.lighter
                                      : "white",
                                    color: isSelected
                                      ? theme.palette.primary.dark
                                      : "black",
                                    "&:hover": {
                                      backgroundColor:
                                        theme.palette.primary.lighter,
                                      color: theme.palette.primary.dark,
                                    },
                                  }}
                                >
                                  <ListItemText primary={library.name} />
                                  {isThisSwitching && (
                                    <CircularProgress size={16} sx={{ ml: 1 }} />
                                  )}
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </Stack>
                      )}
                    </MainCard>
                  </ClickAwayListener>
                </Paper>
              </Transitions>
            )}
          </Popper>
        </Box>
      ) : (
        <></>
      )}
    </>
  );
}
